/**
 * Pure helpers behind the Production Records calendar rail.
 *
 * The rail reuses the Orders month calendar (`WeekMonthCalendar`) with the
 * Orders colour language: green for a day whose record is complete, red for a
 * past day whose record is not. Both sets are derived here from the two
 * endpoints the page already reads — the `/days` rollup for the loaded window
 * and the outstanding-days list, which can reach past that window.
 *
 * Side-effect free; unit-tested in `client/test/lib/production-records/calendar.test.ts`.
 */

import type { ProductionRecordDayWire } from '@/api/production-records'

type DayRow = Pick<ProductionRecordDayWire, 'serviceDate' | 'complete'>

/** yyyy-mm-dd keys of the loaded days whose record is complete (green). */
export function completeDayKeys(rows: ReadonlyArray<DayRow>): Set<string> {
  return new Set(rows.filter((row) => row.complete).map((row) => row.serviceDate))
}

/**
 * yyyy-mm-dd keys that need attention (red): loaded days that are pending and
 * already PAST, unioned with the outstanding-days list. Today is never
 * flagged — its record is still being made — and a complete row is never
 * flagged even if a stale outstanding list still names it.
 */
export function attentionDayKeys(
  rows: ReadonlyArray<DayRow>,
  outstanding: ReadonlyArray<{ serviceDate: string }>,
  today: string,
): Set<string> {
  const complete = completeDayKeys(rows)
  const keys = new Set<string>()
  for (const row of rows) {
    if (!row.complete && row.serviceDate < today) {
      keys.add(row.serviceDate)
    }
  }
  for (const day of outstanding) {
    if (day.serviceDate < today && !complete.has(day.serviceDate)) {
      keys.add(day.serviceDate)
    }
  }
  return keys
}

/**
 * Green across campuses (PROD-1279 part two): a day is complete only when
 * EVERY campus with service that day is. One open campus keeps the day off
 * the green set, whatever the others did.
 */
export function completeDayKeysAcrossCampuses(rowsByCampus: ReadonlyArray<ReadonlyArray<DayRow>>): Set<string> {
  const complete = new Set<string>()
  const pending = new Set<string>()
  for (const rows of rowsByCampus) {
    for (const row of rows) {
      ;(row.complete ? complete : pending).add(row.serviceDate)
    }
  }
  for (const key of pending) {
    complete.delete(key)
  }
  return complete
}

/** Red across campuses: any campus's attention day (`attentionDayKeys`, unioned). */
export function attentionDayKeysAcrossCampuses(
  entries: ReadonlyArray<{ rows: ReadonlyArray<DayRow>; outstanding: ReadonlyArray<{ serviceDate: string }> }>,
  today: string,
): Set<string> {
  const keys = new Set<string>()
  for (const entry of entries) {
    for (const key of attentionDayKeys(entry.rows, entry.outstanding, today)) {
      keys.add(key)
    }
  }
  return keys
}
